import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VisitorsCard } from "@/components/VisitorsCard";
import { Users, TrendingUp, Zap, BarChart3, Sparkles, Bookmark, Shield } from "lucide-react";

function Stat({ label, value, icon: Icon, hint }) {
  return (
    <div className="rounded-lg border border-white/10 bg-[#152040] p-5">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-pink-300 mb-2">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="font-display text-3xl font-bold gradient-sunset-text">{value}</div>
      {hint && <div className="text-[10px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function Sparkline({ series }) {
  if (!series?.length) return null;
  const max = Math.max(1, ...series.map((d) => d.count));
  const W = 640, H = 90;
  const stepX = W / (series.length - 1 || 1);
  const points = series.map((d, i) => `${i * stepX},${H - (d.count / max) * (H - 10) - 5}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-24" preserveAspectRatio="none" data-testid="signups-sparkline">
      <defs>
        <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F9A8D4" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#FDE68A" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polyline
        points={`0,${H} ${points} ${W},${H}`}
        fill="url(#grad)"
        stroke="none"
      />
      <polyline points={points} fill="none" stroke="#FDBA74" strokeWidth="2" />
      {series.map((d, i) => (
        <circle key={i} cx={i * stepX} cy={H - (d.count / max) * (H - 10) - 5} r="2.5" fill="#FDE68A" />
      ))}
    </svg>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (loading || !user) return;
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch((e) => setErr(e?.response?.status || "error"));
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [user, loading]);

  if (loading) return <div className="text-zinc-400">Loading…</div>;
  if (!user) return (
    <div className="min-h-[60vh] grid place-items-center text-center">
      <div>
        <Shield className="w-8 h-8 text-orange-300 mx-auto mb-3" />
        <div className="font-display text-xl font-bold">Admin sign-in required</div>
        <p className="text-sm text-zinc-500 mt-1">Please sign in with your admin Google account.</p>
      </div>
    </div>
  );
  if (err === 401) return (
    <div className="min-h-[60vh] grid place-items-center text-center" data-testid="admin-session-expired">
      <div>
        <Shield className="w-8 h-8 text-orange-300 mx-auto mb-3" />
        <div className="font-display text-xl font-bold">Session expired</div>
        <p className="text-sm text-zinc-500 mt-1 max-w-md mb-4">
          Your admin session has expired. Please sign out and sign back in to continue.
        </p>
        <button
          data-testid="admin-relogin-btn"
          onClick={() => { window.location.href = "/auth"; }}
          className="inline-flex items-center gap-2 text-sm font-semibold rounded-full px-5 py-2 bg-sky-400 hover:bg-sky-300 text-slate-900 transition-colors"
        >
          Sign in again
        </button>
      </div>
    </div>
  );
  if (err === 403 || (user && !user.is_admin && !overview && err !== 401)) return (
    <div className="min-h-[60vh] grid place-items-center text-center" data-testid="admin-forbidden">
      <div>
        <Shield className="w-8 h-8 text-rose-400 mx-auto mb-3" />
        <div className="font-display text-xl font-bold">Access denied</div>
        <p className="text-sm text-zinc-500 mt-1 max-w-md">
          Your account isn't marked as admin. The first user to sign in becomes admin automatically —
          or set <code className="text-orange-300">ADMIN_EMAILS</code> in backend .env.
        </p>
      </div>
    </div>
  );
  if (!overview) return <div className="text-zinc-400">Loading admin metrics…</div>;

  const fmt = (n) => new Intl.NumberFormat("en-IN").format(n);

  return (
    <div className="fade-up">
      <div className="mb-6 flex items-center gap-3">
        <Shield className="w-5 h-5 text-orange-300" />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-pink-300">Admin</div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">Platform overview</h1>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-3 mb-6" data-testid="admin-stats">
        <Stat label="Total signups" value={fmt(overview.totals.users)} icon={Users} hint={`+${overview.signups.last_7_days} this week`} />
        <Stat label="Active today" value={fmt(overview.active.dau)} icon={TrendingUp} hint={`${overview.active.wau} this week · ${overview.active.mau} this month`} />
        <Stat label="Sessions" value={fmt(overview.totals.sessions)} icon={Zap} hint="Total logins recorded" />
        <Stat label="AI questions" value={fmt(overview.totals.ai_interactions)} icon={Sparkles} hint="Total tutor requests" />
      </div>

      <VisitorsCard />

      <div className="grid md:grid-cols-3 gap-3 mb-6">
        <Stat label="Quiz attempts" value={fmt(overview.totals.quiz_attempts)} icon={BarChart3} />
        <Stat label="Practice tests" value={fmt(overview.totals.practice_attempts)} icon={BarChart3} />
        <Stat label="Mental sprints" value={fmt(overview.totals.mental_sessions)} icon={BarChart3} />
      </div>

      <div className="rounded-lg border border-white/10 bg-[#152040] p-5 mb-6" data-testid="admin-signups-chart">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-wider text-pink-300 flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5" /> Signups — last 14 days
          </div>
          <div className="text-xs text-zinc-500">Total: {fmt(overview.signups_series.reduce((a, b) => a + b.count, 0))}</div>
        </div>
        <Sparkline series={overview.signups_series} />
        <div className="flex justify-between text-[10px] text-zinc-500 mt-1 font-mono">
          <span>{overview.signups_series[0]?.date}</span>
          <span>{overview.signups_series[overview.signups_series.length - 1]?.date}</span>
        </div>
      </div>

      <div className="rounded-lg border border-white/10 bg-[#152040] overflow-hidden" data-testid="admin-users-table">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider text-pink-300 flex items-center gap-2">
            <Users className="w-3.5 h-3.5" /> Users ({users.length})
          </div>
          <div className="text-[10px] text-zinc-500">Sorted by last login</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/5">
              <tr>
                <th className="text-left px-4 py-2 font-medium">User</th>
                <th className="text-right px-3 py-2 font-medium">Logins</th>
                <th className="text-right px-3 py-2 font-medium">Quizzes</th>
                <th className="text-right px-3 py-2 font-medium">Tests</th>
                <th className="text-right px-3 py-2 font-medium">Mental</th>
                <th className="text-right px-3 py-2 font-medium">AI</th>
                <th className="text-left px-3 py-2 font-medium">Last login</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-user-${u.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {u.picture ? <img src={u.picture} alt="" className="w-6 h-6 rounded-full" /> : <div className="w-6 h-6 rounded-full bg-gradient-to-br from-pink-300 to-orange-300 grid place-items-center text-[10px] font-bold text-slate-900">{(u.name || "?")[0].toUpperCase()}</div>}
                      <div className="min-w-0">
                        <div className="text-zinc-100 truncate">{u.name}{u.is_admin && <span className="ml-1.5 text-[9px] uppercase tracking-widest text-orange-300 font-semibold">admin</span>}</div>
                        <div className="text-[11px] text-zinc-500 truncate">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-right font-mono text-yellow-300 px-3 py-3">{u.login_count || 0}</td>
                  <td className="text-right font-mono text-zinc-300 px-3 py-3">{u.quiz_count || 0}</td>
                  <td className="text-right font-mono text-zinc-300 px-3 py-3">{u.practice_count || 0}</td>
                  <td className="text-right font-mono text-zinc-300 px-3 py-3">{u.mental_count || 0}</td>
                  <td className="text-right font-mono text-zinc-300 px-3 py-3">{u.ai_count || 0}</td>
                  <td className="px-3 py-3 text-[11px] text-zinc-500 font-mono whitespace-nowrap">
                    {u.last_login ? new Date(u.last_login).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && <div className="text-sm text-zinc-500 text-center py-10">No users yet.</div>}
        </div>
      </div>
    </div>
  );
}
